import { useState, useCallback } from 'react';
import { sortAssignmentsByVisualOrder } from '../../../../utils/leaderUtils';

/**
 * Custom hook to manage undo/redo history and visual sorting for assignments.
 * 
 * @param {Object} options
 * @param {Array} options.assignments - The current list of assignments
 * @param {Function} options.setAssignments - Setter function for assignments
 * @returns {Object} { assignmentHistory, redoHistory, sortAssignmentsByVisualOrder, pushToHistory, handleUndo, handleRedo }
 */
export function useLeaderHistory({ assignments, setAssignments }) {
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [redoHistory, setRedoHistory] = useState([]);


    /**
     * Pushes current assignments to the undo history stack.
     * Limits history to the last 20 actions.
     */
    const pushToHistory = useCallback(() => {
        setAssignmentHistory(prev => [...prev.slice(-19), JSON.parse(JSON.stringify(assignments))]);
        setRedoHistory([]); // Clear redo history on new action
    }, [assignments]);

    /**
     * Reverts to the previous state in the history stack.
     */
    const handleUndo = useCallback(() => {
        if (assignmentHistory.length === 0) return;
        const previousState = assignmentHistory[assignmentHistory.length - 1];
        
        // Push current state to redo history before undoing
        setRedoHistory(prev => [...prev.slice(-19), JSON.parse(JSON.stringify(assignments))]);
        
        setAssignmentHistory(prev => prev.slice(0, -1));
        setAssignments(previousState);
    }, [assignmentHistory, assignments, setAssignments]);

    /**
     * Re-applies an undone state from the redo stack.
     */
    const handleRedo = useCallback(() => {
        if (redoHistory.length === 0) return;
        const nextState = redoHistory[redoHistory.length - 1];
        
        // Push current state back to undo history before redoing
        setAssignmentHistory(prev => [...prev.slice(-19), JSON.parse(JSON.stringify(assignments))]);
        
        setRedoHistory(prev => prev.slice(0, -1));
        setAssignments(nextState);
    }, [redoHistory, assignments, setAssignments]);

    return {
        assignmentHistory,
        redoHistory,
        sortAssignmentsByVisualOrder,
        pushToHistory,
        handleUndo,
        handleRedo
    };
}
